import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser, userAbbreviatedName } from '../../util/data';
import { formatUSCityStateFromAddress } from '../../util/location';
import { createSlug } from '../../util/urlHelpers';
import {
  getDefaultDisplayTimeZone,
  getUsPrimaryTimeZoneLabel,
} from '../../util/displayTimeZone';
import { isBookingProcessAlias } from '../../transactions/transaction';
import {
  fetchListingNextSlot,
  selectListingNextSlot,
} from '../../ducks/listingNextSlot.duck';

import { NamedLink } from '../../components';

import css from './ListingCard.module.css';
import { getCategoryLabel } from '../../containers/ListingPage/SectionAuthorMaybe';

const DEFAULT_DURATION_MINUTES = 30;

// A user is "online" if their last presence ping landed within this window.
// The client pings every 30s, so 60s is one missed ping of headroom.
const ONLINE_WINDOW_MS = 60 * 1000;
// Re-evaluate the online state at this cadence so the badge fades out without
// requiring a re-render trigger from elsewhere.
const ONLINE_TICK_MS = 15 * 1000;

const getInitialsFromDisplayName = name => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  const initials = parts
    .map(p => p[0])
    .join('')
    .toUpperCase();
  return initials.slice(0, 2) || '?';
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Resolve the user's `lastSeenOnline` timestamp from any reasonable shape we may
 * receive: ISO string, epoch ms number, or a Date instance.
 */
const parseLastSeen = raw => {
  if (!raw) return null;
  if (raw instanceof Date) {
    const t = raw.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'string') {
    const t = new Date(raw).getTime();
    return Number.isFinite(t) ? t : null;
  }
  return null;
};

/**
 * Subscribes to a 15s clock tick — no public re-render API in React for "now",
 * so we own a small ticker per-card. Cheap (3-25 cards on a page).
 */
const useNowTick = () => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ONLINE_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return now;
};

const OnlineBadge = ({ lastSeenAt, showOnlineStatus }) => {
  const now = useNowTick();
  // Default behaviour is to show — only opt-out via `showOnlineStatus === false`
  // hides the badge regardless of the recent ping data.
  if (showOnlineStatus === false) return null;
  const lastSeen = parseLastSeen(lastSeenAt);
  if (!lastSeen) return null;
  const isOnline = now - lastSeen <= ONLINE_WINDOW_MS;
  if (!isOnline) return null;
  return (
    <span className={css.live}>
      <span className={css.livePulse} aria-hidden />
      <FormattedMessage id="ListingCard.online" defaultMessage="Online" />
    </span>
  );
};

const StarRow = ({ value, count }) => {
  if (!value) return null;
  return (
    <>
      <span className={css.starRow} aria-label={`${value} out of 5`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M12 2.5 14.97 8.6l6.73.98-4.87 4.74 1.15 6.7L12 17.77l-6 3.16 1.15-6.7L2.27 9.58l6.73-.98L12 2.5Z" />
        </svg>
        <strong>{Number(value).toFixed(1)}</strong>
      </span>
      {count != null && count > 0 ? (
        <span className={css.reviews}>({count})</span>
      ) : null}
    </>
  );
};

/**
 * Format a future Date as "Today, 4:30 PM (ET)" / "Tomorrow, 9:00 AM (ET)" / "Mar 14, 2:00 PM (ET)"
 * in the same display timezone as the listing page booking panel (ET by default).
 */
const formatNextSlot = (date, intl, displayTimeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const tzOpts = { timeZone: displayTimeZone };
  const time = intl.formatDate(date, { hour: 'numeric', minute: 'numeric', ...tzOpts });
  const tzLabel = getUsPrimaryTimeZoneLabel(displayTimeZone) || 'ET';
  const timeWithZone = `${time} (${tzLabel})`;

  const formatYMD = d =>
    intl.formatDate(d, { year: 'numeric', month: '2-digit', day: '2-digit', ...tzOpts });
  const todayKey = formatYMD(new Date());
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
  const tomorrowKey = formatYMD(tomorrow);
  const targetKey = formatYMD(date);

  if (targetKey === todayKey) return `Today, ${timeWithZone}`;
  if (targetKey === tomorrowKey) return `Tomorrow, ${timeWithZone}`;
  const dateLabel = intl.formatDate(date, { month: 'short', day: 'numeric', ...tzOpts });
  return `${dateLabel}, ${timeWithZone}`;
};

const NextSlot = ({ listingId, listingTimezone, isInquiry }) => {
  const dispatch = useDispatch();
  const entry = useSelector(state => selectListingNextSlot(state, listingId));
  const intl = useIntl();
  const displayTimeZone = getDefaultDisplayTimeZone(listingTimezone);

  useEffect(() => {
    if (!listingId || isInquiry) return;
    dispatch(fetchListingNextSlot({ listingId }));
  }, [dispatch, listingId, isInquiry]);

  // Inquiry-style listings don't have bookable slots — nothing to show.
  if (isInquiry) return null;

  const slotDate = entry?.nextSlot ? new Date(entry.nextSlot) : null;
  const isLoading = !entry || (entry.inFlight && !entry.fetchedAt);

  return (
    <div className={css.slot}>
      <span className={css.slotDot} aria-hidden />
      <div className={css.slotContent}>
        {isLoading ? (
          <>
            <span className={css.slotLabel}>Next slot</span>
            <span className={css.slotLoading} aria-label="Loading next slot" />
          </>
        ) : slotDate ? (
          <>
            <span className={css.slotLabel}>Next slot</span>
            <strong className={css.slotTime}>
              {formatNextSlot(slotDate, intl, displayTimeZone)}
            </strong>
          </>
        ) : (
          <span className={css.slotEmpty}>No upcoming availability</span>
        )}
      </div>
    </div>
  );
};

const Avatar = ({ author, displayName }) => {
  const hasImage = !!author?.profileImage?.attributes?.variants?.['scaled-small']?.url
    || !!author?.profileImage?.attributes?.variants?.['scaled-medium']?.url
    || !!author?.profileImage?.attributes?.variants?.['square-small']?.url;
  const variants = author?.profileImage?.attributes?.variants || {};
  const url =
    variants['scaled-medium']?.url ||
    variants['scaled-small']?.url ||
    variants['square-small2x']?.url ||
    variants['square-small']?.url ||
    null;

  if (hasImage && url) {
    return (
      <span className={css.avatar} aria-hidden>
        <img className={css.avatarImage} alt={displayName} src={url} />
      </span>
    );
  }

  const initials =
    userAbbreviatedName(author, getInitialsFromDisplayName(displayName || '')) ||
    getInitialsFromDisplayName(displayName) ||
    '?';

  return (
    <span className={css.avatar} aria-hidden>
      {initials}
    </span>
  );
};

const PriceBlock = ({ price, publicData, config, intl, listingTypeConfig }) => {
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) return null;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  if (!formattedPrice) return null;

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const priceVariants = publicData?.priceVariants || [];
  const hasMultiplePriceVariants = isPriceVariationsInUse && priceVariants.length > 1;

  const durationMinutes =
    priceVariants?.[0]?.bookingLengthInMinutes ??
    publicData?.defaultDurationMinutes ??
    DEFAULT_DURATION_MINUTES;

  return (
    <span className={css.price} title={priceTitle}>
      <strong>{formattedPrice}</strong>
      {isBookable ? (
        <span>
          / {durationMinutes} min
        </span>
      ) : publicData?.unitType ? (
        <span>/ {publicData.unitType}</span>
      ) : null}
    </span>
  );
};

/**
 * ListingCard
 *
 * A single expert/listing card matching the homepage hero card design:
 *   - avatar (left), name + meta (centered), online badge (right)
 *   - category tag + star rating + review count
 *   - "Next slot" panel (lazily fetched via `sdk.timeslots.query`)
 *   - price + Book CTA
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {Object} props.listing API entity (listing or ownListing)
 * @param {Function?} props.setActiveListing
 * @returns {JSX.Element}
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const { className, rootClassName, listing, setActiveListing } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id?.uuid;
  const { title = '', price, publicData, availabilityPlan } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const lastSeenOnline = author.attributes?.profile?.metadata?.lastSeenOnline;
  // Author opted out of online status (set in Profile Settings). Default = show.
  const showOnlineStatus = author.attributes?.profile?.publicData?.showOnlineStatus !== false;

  const listingMetadata = currentListing?.attributes?.metadata || {};

  const {
    listingType,
    yearsOfExperience,
    experience,
    businessName,
    transactionProcessAlias,
  } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(c => c.listingType === listingType);
  const categoryConfiguration = config.categoryConfiguration || {};
  const categoryLabel = getCategoryLabel(
    publicData || {},
    categoryConfiguration,
    validListingTypes
  );

  const businessNameText = typeof businessName === 'string' ? businessName.trim() : '';
  const locationLabel = formatUSCityStateFromAddress(publicData?.location?.address);
  const yearsExperience =
    yearsOfExperience != null && yearsOfExperience !== ''
      ? yearsOfExperience
      : experience != null && experience !== ''
      ? experience
      : null;

  const averageRating = Number(listingMetadata?.averageRating || 0);
  const reviewCount = Number(listingMetadata?.reviewCount || 0);

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // "Inquiry" listings have no time slots — skip the Next slot fetch entirely.
  const isInquiry = !isBookingProcessAlias(transactionProcessAlias);
  const listingTimezone = availabilityPlan?.timezone;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <article className={css.card} {...(setActivePropsMaybe || {})}>
        <div className={css.head}>
          <Avatar author={author} displayName={authorName} />
          <div className={css.id}>
            <strong className={css.name}>{authorName}</strong>
            {businessNameText ? (
              <span className={css.meta}>
                <FormattedMessage
                  id="ListingCard.ownerOf"
                  values={{ businessName: businessNameText }}
                />
              </span>
            ) : null}
          </div>
          <OnlineBadge lastSeenAt={lastSeenOnline} showOnlineStatus={showOnlineStatus} />
        </div>

        {(categoryLabel || locationLabel || yearsExperience || averageRating > 0) ? (
          <div className={css.tagRow}>
            {categoryLabel ? <span className={css.tag}>{categoryLabel}</span> : null}
            {locationLabel ? <span className={css.pill}>{locationLabel}</span> : null}
            {yearsExperience ? (
              <span className={css.pill}>
                {String(yearsExperience).match(/^\d+$/) ? (
                  <FormattedMessage
                    id="ListingPage.yearsExperience"
                    values={{ years: yearsExperience }}
                  />
                ) : (
                  yearsExperience
                )}
              </span>
            ) : null}
            <StarRow value={averageRating} count={reviewCount} />
          </div>
        ) : null}

        <NextSlot
          listingId={id}
          listingTimezone={listingTimezone}
          isInquiry={isInquiry}
        />

        <div className={css.foot}>
          <PriceBlock
            price={price}
            publicData={publicData}
            config={config}
            intl={intl}
            listingTypeConfig={foundListingTypeConfig}
          />
          <span className={css.book}>
            <FormattedMessage id="ListingCard.bookCall" />
          </span>
        </div>
      </article>
    </NamedLink>
  );
};

export default ListingCard;
